import { z } from 'zod';

/** As chaves com que o app Flutter libera o agendamento: o hub e cada serviço. */
const HUB_FLAG = 'mobility_scheduling';
const COOPERATIVE_FLAG = 'mobility_scheduling_coop_taxi_enable';
const CAR_DRIVER_FLAG = 'mobility_scheduling_car_driver_enable';

/** As flags do feature-flag-service (`currentFeatures(system: USER_APP)`) que o agendamento de
 *  carro lê. O customer-api repassa só estas; o resto da lista do legado não sai de lá. */
export const MOBILITY_SCHEDULING_FEATURE_FLAGS = [
  HUB_FLAG,
  COOPERATIVE_FLAG,
  CAR_DRIVER_FLAG,
] as const;

export interface MobilitySchedulingAccessInput {
  /** `policyAccess` contém `mobility`; `null` = não resolvido. */
  mobilityPolicy: boolean | null;
  /** `currentFeatures(USER_APP)` já filtradas pelo customer-api; `null` = não resolvido. */
  featureFlags: readonly string[] | null;
  /** `spendingBalanceOverflow.blocked`; `null` = não resolvido. */
  spendingBlocked: boolean | null;
}

export interface MobilitySchedulingEntry {
  cooperative: boolean;
  carDriver: boolean;
}

const HIDDEN: MobilitySchedulingEntry = { cooperative: false, carDriver: false };

// O veredito do agendamento no `/me` (#7323): a porta "Agendamento" da Home some só com
// `offer: false`, e `unavailable` (só com `offer: false`) mantém a porta — a tela avisa que não deu
// para verificar. Chave AUSENTE = oferecido ou nada se sabe: a porta segue a política, como antes.
export const MobilityScheduleEntrySchema = z
  .object({ offer: z.boolean(), unavailable: z.literal(true).optional() })
  .strict();
export type MobilityScheduleEntry = z.infer<typeof MobilityScheduleEntrySchema>;

/** Por que o agendamento não foi oferecido (#7323): `no_*`/`spending_blocked` é uma leitura que
 *  respondeu "não"; `unresolved_*`, a primeira leitura que não respondeu. */
export type MobilitySchedulingHiddenReason =
  | 'no_policy'
  | 'spending_blocked'
  | 'no_hub_flag'
  | 'no_service_flag'
  | 'unresolved_policy'
  | 'unresolved_flags'
  | 'unresolved_spending';

export interface MobilitySchedulingVerdict {
  entry: MobilitySchedulingEntry;
  /** Uma leitura falhou e o que se SABE aponta para o agendamento: "tente de novo", nunca libera. */
  unavailable?: true;
  /** Ausente quando algum serviço abre. */
  reason?: MobilitySchedulingHiddenReason;
}

/** A primeira leitura não resolvida, na ordem da regra. */
function unresolvedOf(
  input: MobilitySchedulingAccessInput,
): MobilitySchedulingHiddenReason | undefined {
  if (input.mobilityPolicy === null) return 'unresolved_policy';
  if (input.featureFlags === null) return 'unresolved_flags';
  if (input.spendingBlocked === null) return 'unresolved_spending';
  return undefined;
}

/**
 * Quais serviços do agendamento de carro aparecem — a MESMA regra no chat e nas telas: a do hub
 * do legado (flag do hub + flag do serviço + política `mobility` + gastos não bloqueados), com o
 * motivo de cada recusa. Dado não resolvido NUNCA abre; o bloqueio de gastos só libera quando a
 * resposta diz que NÃO há bloqueio. `unavailable` (#7323), como na `mobility99Verdict`: só quando
 * nenhuma leitura resolvida descarta o agendamento e ao menos uma aponta para ele (a política
 * `mobility` ou as flags com um serviço).
 */
export function mobilitySchedulingVerdict(
  input: MobilitySchedulingAccessInput,
): MobilitySchedulingVerdict {
  const { mobilityPolicy, featureFlags, spendingBlocked } = input;
  if (mobilityPolicy === false) return { entry: HIDDEN, reason: 'no_policy' };
  if (spendingBlocked === true) return { entry: HIDDEN, reason: 'spending_blocked' };
  if (featureFlags && !featureFlags.includes(HUB_FLAG))
    return { entry: HIDDEN, reason: 'no_hub_flag' };
  const services = featureFlags && {
    cooperative: featureFlags.includes(COOPERATIVE_FLAG),
    carDriver: featureFlags.includes(CAR_DRIVER_FLAG),
  };
  if (services && !services.cooperative && !services.carDriver)
    return { entry: HIDDEN, reason: 'no_service_flag' };
  const unresolved = unresolvedOf(input);
  if (unresolved) {
    const pointsTo = mobilityPolicy === true || services !== null;
    return { entry: HIDDEN, reason: unresolved, ...(pointsTo ? { unavailable: true } : {}) };
  }
  // Sem leitura em aberto, `services` existe (as flags resolveram).
  return { entry: services ?? HIDDEN };
}

export function mobilitySchedulingEntry(
  input: MobilitySchedulingAccessInput,
): MobilitySchedulingEntry {
  return mobilitySchedulingVerdict(input).entry;
}
